import { types } from "api";

/**
 * When `permissions` is set to `types.RoleType[]`, the route will only be
 * visible to users with the specified permissions.
 *
 * When `permissions` is `"authenticated"`, the route will be visible to all
 * authenticated users.
 *
 * When `"unauthenticated"`, the route will only be visible to unauthenticated
 * users.
 */
export type RoutePermissions =
	| types.RoleType[]
	| "authenticated"
	| "unauthenticated"
	| "all";

export interface Route {
	readonly _?: {
		[key: string]: Route;
	};
	readonly path: string;
	readonly name: string;
	readonly permissions: RoutePermissions;
}

export const routes = {
	path: "/",
	name: "Home",
	permissions: "all",
	_: {
		signIn: {
			path: "/sign-in",
			name: "Sign In",
			permissions: "unauthenticated",
		},
		register: {
			path: "/register",
			name: "Register",
			permissions: "unauthenticated",
		},
		googleCompleteProfile: {
			path: "/google-complete-profile",
			name: "Complete Profile",
			permissions: "all",
		},
		people: {
			path: "/people",
			name: "People",
			permissions: ["admin", "teacher"] as types.RoleType[],
			_: {
				me: {
					path: "/people/me",
					name: "Me",
					permissions: "authenticated",
					_: {
						changePassword: {
							path: "/people/me/change-password",
							name: "Change Password",
							permissions: "authenticated",
						},
						changeEmail: {
							path: "/people/me/change-email",
							name: "Change Email",
							permissions: "authenticated",
						},
						changeName: {
							path: "/people/me/change-name",
							name: "Change Name",
							permissions: "authenticated",
						},
						updateChild: {
							path: "/people/me/update-child",
							name: "Update Child Relation",
							permissions: "authenticated",
						},
					},
				},
				createAccounts: {
					path: "/people/create",
					name: "Create Accounts",
					permissions: ["admin", "admin-teacher", "teacher"] as types.RoleType[],
				},
				userProfile: {
					path: "/people/view/:userId",
					name: "User Profile",
					permissions: ["admin", "teacher", "parent"] as types.RoleType[],
					_: {
						resetPassword: {
							path: "/people/view/:userId/reset-password",
							name: "Reset Password",
							permissions: ["admin"] as types.RoleType[],
						},
						changeEmail: {
							path: "/people/view/:userId/change-email",
							name: "Change Email",
							permissions: ["admin"] as types.RoleType[],
						},
						changeName: {
							path: "/people/view/:userId/change-Name",
							name: "Change Name",
							permissions: ["admin"] as types.RoleType[],
						},
						updateChild: {
							path: "/people/view/:userId/update-child",
							name: "Update Child Relation",
							permissions: ["admin", "teacher", "parent"] as types.RoleType[],
						},
					},
				},
			},
		},
		groups: {
			path: "/groups",
			name: "Groups",
			permissions: "authenticated",
			_: {
				create: {
					path: "/groups/create",
					name: "Create Group",
					permissions: ["admin", "teacher"] as types.RoleType[],
				},
				edit: {
					path: "/groups/edit/:groupId",
					name: "Edit Group",
					permissions: ["admin", "teacher"] as types.RoleType[],
				},
				view: {
					path: "/groups/view/:groupId",
					// This is plural because `ViewGroupsPage` views multiple groups at a time
					name: "View Groups",
					permissions: "authenticated",
					_: {
						leaderboard: {
							path: "/groups/view/:groupId/leader",
							name: "Leaderboard",
							permissions: "authenticated",
						},
					},
				},
			},
		},
		assignments: {
			path: "/assignments",
			name: "Assignments",
			permissions: "authenticated",
			_: {
				create: {
					path: "/assignments/create",
					name: "Create Assignment",
					permissions: ["admin", "teacher"] as types.RoleType[],
				},
				edit: {
					path: "/assignments/edit/:assignmentId",
					name: "Edit Assignment",
					permissions: ["admin", "teacher"] as types.RoleType[],
				},
				view: {
					path: "/assignments/view/:assignmentId",
					name: "View Assignment",
					permissions: "authenticated",
				},
			},
		},
		trackAndReport: {
			path: "/track-and-report",
			name: "Tracking",
			permissions: ["admin", "teacher"] as types.RoleType[],
			_: {
				trackGroup: {
					path: "/track-and-report/groups/:groupId",
					name: "Track Group",
					permissions: ["admin", "teacher"] as types.RoleType[],
				},
			},
		},
		metrics: {
			path: "/metrics",
			name: "Metrics",
			permissions: "authenticated",
			_: {
				Groups: {
					path: "/metrics/groups",
					name: "View Group List",
					permissions: ["admin", "teacher"] as types.RoleType[],
					_: {
						viewGroup: {
							path: "/metrics/groups/:groupId",
							name: "View Group",
							permissions: ["admin", "teacher"] as types.RoleType[],
						},
					},
				},
				Students: {
					path: "/metrics/students",
					name: "View Student List",
					permissions: ["admin", "teacher"] as types.RoleType[],
				},
				RegisteredUsers: {
					path: "/metrics/registered-users",
					name: "View Registered Users List",
					permissions: ["admin"] as types.RoleType[],
				},
				viewMetrics: {
					path: "/metrics/view/:userId",
					name: "View Metrics",
					permissions: "authenticated",
				},
				viewGroupMetrics: {
					path: "/metrics/viewGroup/:groupId",
					name: "View Group Metrics",
					permissions: ["admin", "teacher"] as types.RoleType[],
				},
				School: {
					path: "/metrics/viewSchool",
					name: "View School Metrics",
					permissions: ["admin", "teacher"] as types.RoleType[],
				},
				viewSchoolGradeMetrics: {
					path: "/metrics/viewSchool/:schoolId",
					name: "View School Grade Metrics",
					permissions: ["admin", "teacher"] as types.RoleType[],
				},
			},
		},
		result: {
			path: "/result",
			name: "Result",
			permissions: "authenticated",
			_: {
				Groups: {
					path: "/result/groups",
					name: "View Group List",
					permissions: ["admin", "teacher"] as types.RoleType[],
					_: {
						viewGroup: {
							path: "/result/groups/:groupId",
							name: "View Group",
							permissions: ["admin", "teacher"] as types.RoleType[],
						},
						viewGroupResult: {
							path: "/result/groups/view/:groupId",
							name: "View Group Result",
							permissions: ["admin", "teacher"] as types.RoleType[],
						},
					},
				},
				Students: {
					path: "/result/students",
					name: "View Student List",
					permissions: ["admin", "teacher"] as types.RoleType[],
				},
				viewResult: {
					path: "/result/view/:userId",
					name: "View Results",
					permissions: "authenticated",
				},
				addEvaluation: {
					path: "/result/evaluation/:userId",
					name: "Add Evaluation",
					permissions: ["admin", "teacher"] as types.RoleType[],
				},
			},
		},
	},
} as const;

export const errorRoutes = {
    _: {
        forbidden: {
            path: "/403",
            name: "Forbidden",
            permissions: "all",

            // TODO: Remove once routing issues are resolved.
            redirectTo: "/",
        },
        error: {
            path: "/404",
            name: "Error",
            permissions: "all",

            // TODO: Remove once routing issues are resolved.
            redirectTo: "/",
        },
    },
} as const;
